/* PureState reactive glow engine + cursor dot.
   Glow surfaces: any element with [data-glow] gets --glow-x/--glow-y/--glow-o
   eased through one shared rAF loop (the section CSS decides how to render them).
   Desktop (fine pointer): the light follows the cursor over/near each surface.
   Touch: the light drifts diagonally (under 45 deg — more sideways than down)
   with scroll progress; the INCI modal is driven by its own internal scroll.
   Cursor dot: .ps-cursor-dot trails the pointer site-wide (fine pointers only).
   Re-registers surfaces on theme-editor section re-renders.
   prefers-reduced-motion: nothing runs. */
(function () {
  'use strict';

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var canObserve = 'IntersectionObserver' in window;

  var glows = [];
  var dot = null;
  var dotState = { x: -100, y: -100, tx: -100, ty: -100, o: 0, to: 0 };
  var rafId = null;

  function frame() {
    rafId = null;
    var active = false;
    for (var i = 0; i < glows.length; i++) {
      var it = glows[i];
      it.cx += (it.tx - it.cx) * 0.1;
      it.cy += (it.ty - it.cy) * 0.1;
      it.co += (it.to - it.co) * 0.08;
      it.el.style.setProperty('--glow-x', (it.cx * 100).toFixed(2) + '%');
      it.el.style.setProperty('--glow-y', (it.cy * 100).toFixed(2) + '%');
      it.el.style.setProperty('--glow-o', it.co.toFixed(3));
      if (
        Math.abs(it.tx - it.cx) > 0.001 ||
        Math.abs(it.ty - it.cy) > 0.001 ||
        Math.abs(it.to - it.co) > 0.002
      ) active = true;
    }
    if (dot) {
      dotState.x += (dotState.tx - dotState.x) * 0.22;
      dotState.y += (dotState.ty - dotState.y) * 0.22;
      dotState.o += (dotState.to - dotState.o) * 0.15;
      dot.style.transform = 'translate3d(' + dotState.x.toFixed(1) + 'px,' + dotState.y.toFixed(1) + 'px,0)';
      dot.style.opacity = dotState.o.toFixed(3);
      if (
        Math.abs(dotState.tx - dotState.x) > 0.3 ||
        Math.abs(dotState.ty - dotState.y) > 0.3 ||
        Math.abs(dotState.to - dotState.o) > 0.01
      ) active = true;
    }
    if (active) schedule();
  }
  function schedule() {
    if (rafId === null) rafId = requestAnimationFrame(frame);
  }

  function makeItem(el) {
    return { el: el, cx: 0.5, cy: 0.15, co: 0, tx: 0.5, ty: 0.15, to: 0, visible: false, io: null };
  }

  function driveScroll(it, p) {
    p = Math.max(0, Math.min(1, p));
    it.tx = 0.1 + p * 0.8; /* x sweeps 0.8 */
    it.ty = 0.2 + p * 0.5; /* y sweeps 0.5 -> ~32 deg, under 45 */
  }

  function observeTouch(it) {
    it.io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        it.visible = en.isIntersecting;
        it.to = en.isIntersecting ? 0.55 : 0;
      });
      schedule();
    }, { threshold: 0.1 });
    it.io.observe(it.el);
  }

  /* The theme editor replaces section DOM on every setting change — rebuild
     the registry, keeping eased state for elements that survived. */
  function rebuild() {
    var prev = glows;
    var next = [];
    document.querySelectorAll('[data-glow]').forEach(function (el) {
      var existing = null;
      for (var i = 0; i < prev.length; i++) {
        if (prev[i].el === el) { existing = prev[i]; break; }
      }
      if (existing) {
        next.push(existing);
      } else {
        var it = makeItem(el);
        if (!fine && canObserve) observeTouch(it);
        next.push(it);
      }
    });
    prev.forEach(function (it) {
      if (next.indexOf(it) === -1 && it.io) it.io.disconnect();
    });
    glows = next;
  }

  function fadeAll() {
    for (var i = 0; i < glows.length; i++) glows[i].to = 0;
    dotState.to = 0;
    schedule();
  }

  function init() {
    rebuild();
    document.addEventListener('shopify:section:load', rebuild);
    document.addEventListener('shopify:section:unload', rebuild);

    dot = fine ? document.querySelector('.ps-cursor-dot') : null;
    if (dot) dot.style.display = 'block'; /* inline beats env-injected hiding; dot stays invisible via opacity 0 until the pointer moves */
    if (!glows.length && !dot && !window.Shopify) return;

    if (fine) {
      var NEAR = 90;
      window.addEventListener('pointermove', function (e) {
        /* While a modal is open, only its own surface may glow — background
           cards would shine through the translucent backdrop. */
        var modalOpen = !!document.querySelector('dialog[open]');
        for (var i = 0; i < glows.length; i++) {
          var it = glows[i];
          if (modalOpen && !it.el.closest('dialog')) { it.to = 0; continue; }
          var r = it.el.getBoundingClientRect();
          if (!r.width || !r.height) { it.to = 0; continue; }
          if (
            e.clientX > r.left - NEAR && e.clientX < r.right + NEAR &&
            e.clientY > r.top - NEAR && e.clientY < r.bottom + NEAR
          ) {
            it.tx = (e.clientX - r.left) / r.width;
            it.ty = (e.clientY - r.top) / r.height;
            it.to = 1;
          } else {
            it.to = 0;
          }
        }
        if (dot) {
          if (dotState.o < 0.05) { dotState.x = e.clientX; dotState.y = e.clientY; }
          dotState.tx = e.clientX;
          dotState.ty = e.clientY;
          /* The dialog's top layer paints over any z-index — hide the dot
             while a modal is open instead of letting it dim behind the backdrop. */
          dotState.to = modalOpen ? 0 : 1;
        }
        schedule();
      }, { passive: true });
      /* Safari doesn't fire pointerleave when the page scrolls under a still
         cursor — fade the glows; the next real pointermove re-lights them. */
      window.addEventListener('scroll', function () {
        for (var i = 0; i < glows.length; i++) glows[i].to = 0;
        schedule();
      }, { passive: true });
      document.documentElement.addEventListener('pointerleave', fadeAll);
      /* Entering an iframe (chat widgets, video embeds) swallows pointer
         events without ever firing pointerleave on <html> — catch it here. */
      document.addEventListener('pointerout', function (e) {
        if (!e.relatedTarget || (e.relatedTarget.tagName && e.relatedTarget.tagName === 'IFRAME')) fadeAll();
      });
    } else if (canObserve) {
      window.addEventListener('scroll', function () {
        for (var i = 0; i < glows.length; i++) {
          var it = glows[i];
          if (!it.visible || it.el.closest('dialog')) continue;
          var r = it.el.getBoundingClientRect();
          driveScroll(it, 1 - r.bottom / (window.innerHeight + r.height));
        }
        schedule();
      }, { passive: true });
      /* Page scroll is locked while the INCI modal is open — drive its glow
         from the modal's internal scroll instead. */
      var modalBody = document.querySelector('.inci-modal__body');
      if (modalBody) {
        modalBody.addEventListener('scroll', function () {
          var max = modalBody.scrollHeight - modalBody.clientHeight;
          for (var i = 0; i < glows.length; i++) {
            var it = glows[i];
            if (!it.el.closest('dialog')) continue;
            driveScroll(it, max > 0 ? modalBody.scrollTop / max : 0.5);
          }
          schedule();
        }, { passive: true });
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
